import {
  HorseCardComponentProps,
  InPlayProps,
  SelectionMade,
  NotFocused,
  PrePlayState,
  ResultProps,
  Focused,
  HorseCardState,
  PrePlayProps,
  HorseInfo,
} from './HorseCard.types';
import { getAttributeValue, Selection } from '../../interfaces/sports';
import { useGetOddsFromPrice } from '../../store/options/optionsStore';
import { useCallback } from 'react';

// state type checks
export const isInPlayState = (state: HorseCardState): state is 'in-play' => state === 'in-play';
export const isResultedState = (state: HorseCardState): state is 'resulted' => state === 'resulted';
export const isSelectionMadeState = (state: HorseCardState): state is 'selection-made' => state === 'selection-made';
export const isFocusedState = (state: HorseCardState): state is 'focused' => state === 'focused';
export const isNotFocusedState = (state: HorseCardState): state is 'not-focused' => state === 'not-focused';
export const isPrePlayState = (state: HorseCardState): state is PrePlayState => {
  return isSelectionMadeState(state) || isFocusedState(state) || isNotFocusedState(state);
};

// props type checks
export const isInPlay = (result: HorseCardComponentProps): result is InPlayProps =>
  'state' in result && isInPlayState(result.state);
export const isResulted = (result: HorseCardComponentProps): result is ResultProps =>
  'state' in result && isResultedState(result.state);
export const isSelectionMade = (result: HorseCardComponentProps): result is SelectionMade =>
  'state' in result && isSelectionMadeState(result.state);
export const isFocused = (result: HorseCardComponentProps): result is Focused =>
  'state' in result && isFocusedState(result.state);
export const isNotFocused = (result: HorseCardComponentProps): result is NotFocused =>
  'state' in result && isNotFocusedState(result.state);
export const isPrePlay = (result: HorseCardComponentProps): result is PrePlayProps =>
  'state' in result && isPrePlayState(result.state);

export const isWin = (result: HorseCardComponentProps): boolean => {
  return isResulted(result) && (result.won || (result.position === 'NR' && result.replaced.won));
};

export const useGetHorseInfo = () => {
  const getOddsFromPrice = useGetOddsFromPrice();

  return useCallback(
    (selection: Selection): HorseInfo => {
      const age = getAttributeValue({ attributes: selection.attributes, name: 'age' });
      return {
        jerseyIcon: getAttributeValue({ attributes: selection.attributes, name: 'silkImage' }),
        number: Number(selection.selectionCode),
        horseName: selection.selectionName,
        jockeyName: getAttributeValue({ attributes: selection.attributes, name: 'jockeyName' }),
        trainerName: getAttributeValue({ attributes: selection.attributes, name: 'trainerName' }),
        form: getAttributeValue({ attributes: selection.attributes, name: 'form' }),
        age: age ? Number(age) : undefined,
        weight: getAttributeValue({ attributes: selection.attributes, name: 'weight' }),
        odds: getOddsFromPrice(selection?.price),
        reserve: Boolean(getAttributeValue({ attributes: selection.attributes, name: 'reserve' })),
      };
    },
    [getOddsFromPrice],
  );
};
